import {inject, Injectable, Injector} from '@angular/core';
import {LoggingPublisherInterface} from "./logging-publisher.interface";
import {LogMessage} from "./log-message";
import {environment} from "../../../environments/environment";
import {ErrorService} from "../error/error.service";
import {isDefined, nonBlank, safeSplit} from "../../libs/utils/epic.utils";
import {LogLevel} from "./log-level.enum";

@Injectable({providedIn: 'root'})
export class LoggingPublisherApiService implements LoggingPublisherInterface {

  private static readonly LIB_FILES: RegExp = /Subscriber\.js|core\.js|zone\.js/i;
  private static readonly ACC_FILES: string[] = [
    '.service.ts',
    '.repo.ts',
    '.component.ts',
    '.handler.ts',
    '.interceptor.ts',
    '.provider.ts'
  ];

  private readonly injector: Injector = inject(Injector);

  private readonly enabled: boolean = environment.api.logging.enabled;
  private readonly logLevel: LogLevel = environment.api.logging.loglevel;
  private errorService: ErrorService | undefined;

  public isEnabled(): boolean {
    return this.enabled;
  }

  public publish(log: LogMessage): void {
    if (this.isLogLevel(log.getLevel())) {
      this.getErrorService().then((s) => {
        const service: string = this.getFirstService(log.getStack()) ?? 'LoggingService';
        const clazz: string = nonBlank(log.getClazz()) ? log.getClazz()! : service;
        s.error(log.getLevel(), log.buildMessage(), clazz, log.getType(), log.getCode());
      });
    }
  }

  // Load ErrorService lazy, otherwise error NG0200 ‘Circular dependencies’ occurs (only on the first call)
  private getErrorService(): Promise<ErrorService> {
    if (isDefined(this.errorService)) {
      return Promise.resolve(this.errorService);
    }
    return new Promise((resolve) => {
      setTimeout(() => {
        this.errorService = this.injector.get(ErrorService);
        resolve(this.errorService);
      }, 1000);
    });
  }

  private getFirstService(stack: string | undefined): string | undefined {
    if (isDefined(stack)) {
      const lines: string[] = safeSplit(stack, '\n')
        .filter((l) => nonBlank(l))
        .map((l) => l.trim());
      for (const line of lines) {
        if (!LoggingPublisherApiService.LIB_FILES.test(line)) {
          for (const accFile of LoggingPublisherApiService.ACC_FILES) {
            if (line.includes(accFile)) {
              return line;
            }
          }
        }
      }
    }
    return undefined;
  }

  private isLogLevel(lv: LogLevel): boolean {
    switch (this.logLevel) {
      case LogLevel.TRACE:
        return lv === LogLevel.TRACE || lv === LogLevel.DEBUG || lv === LogLevel.INFO || lv === LogLevel.WARN || lv === LogLevel.ERROR;
      case LogLevel.DEBUG:
        return lv === LogLevel.DEBUG || lv === LogLevel.INFO || lv === LogLevel.WARN || lv === LogLevel.ERROR;
      case LogLevel.INFO:
        return lv === LogLevel.INFO || lv === LogLevel.WARN || lv === LogLevel.ERROR;
      case LogLevel.WARN:
        return lv === LogLevel.WARN || lv === LogLevel.ERROR;
      case LogLevel.ERROR:
        return lv === LogLevel.ERROR;
      case LogLevel.OFF:
        console.log("Logging is disabled");
        return false;
      default:
        console.log("LogLevel not set. Fallback to disabled");
        return false;
    }
  }
}
